import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '../shared/Icons';

// shadcn/ui Pagination primitives, themed onto our design tokens and Icon set. Standard shadcn
// pulls `buttonVariants` from a ui/button and lucide icons — we have neither (our primitives live
// in components/shared), so the link styling is inlined against our tokens and the chevrons/ellipsis
// come from our Icon set. Items are BUTTONS (not anchors): our pagination is client-side (slicing a
// fetched list via usePagination), not URL-routed, so there's nothing to navigate to.

function Pagination({ className, ...props }: ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
}

function PaginationContent({ className, ...props }: ComponentProps<'ul'>) {
  return <ul className={cn('flex flex-row items-center gap-1', className)} {...props} />;
}

function PaginationItem(props: ComponentProps<'li'>) {
  return <li {...props} />;
}

type PaginationLinkProps = { isActive?: boolean } & ComponentProps<'button'>;

// One page control (a number, or prev/next). `isActive` marks the current page — styled like the
// rest of the console's selected-affordance (surface + border + shadow), inactive stays quiet.
function PaginationLink({ className, isActive, type, ...props }: PaginationLinkProps) {
  return (
    <button
      type={type ?? 'button'}
      aria-current={isActive ? 'page' : undefined}
      data-active={isActive ? '' : undefined}
      className={cn(
        'inline-flex h-9 min-w-9 items-center justify-center gap-1 rounded-sm border px-2.5 text-[13px] font-medium transition-colors',
        'disabled:pointer-events-none disabled:opacity-40',
        isActive
          ? 'border-line bg-surface text-ink shadow-1'
          : 'border-transparent text-ink-2 hover:bg-surface-2 hover:text-ink',
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({ className, ...props }: ComponentProps<'button'>) {
  return (
    <PaginationLink aria-label="Go to previous page" className={cn('px-2', className)} {...props}>
      <Icon.chevronLeft className="h-4 w-4" />
      <span className="hidden sm:inline">Previous</span>
    </PaginationLink>
  );
}

function PaginationNext({ className, ...props }: ComponentProps<'button'>) {
  return (
    <PaginationLink aria-label="Go to next page" className={cn('px-2', className)} {...props}>
      <span className="hidden sm:inline">Next</span>
      <Icon.chevronRight className="h-4 w-4" />
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      className={cn('flex h-9 w-9 items-center justify-center text-muted', className)}
      {...props}
    >
      <Icon.more className="h-4 w-4" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
